import { useState, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FileUtils, PreLessonMaterialType } from "@live-writing/models";
import ApiClient from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Trash2, Share2, FileText, Copy, Check, ArrowLeft, Pencil, X } from "lucide-react";
import { FileUpload } from "@/components/file-upload";
import { useFileUpload, type UploadedFile } from "@/hooks/use-file-upload";
import { usePreLessonStore } from "@/hooks/use-pre-lesson-store";
import type { PreLessonMaterialValues } from "@live-writing/models";
import { Link } from "react-router-dom";

interface Material {
  id: string;
  title: string;
  link: string;
  type: PreLessonMaterialType;
  s3_key?: string;
  file_name?: string;
  content_type?: string;
}

interface PreLessonResponse {
  pre_lesson: {
    id: number;
    code: string;
    title: string;
    subject: string;
    topic: string;
    materials?: PreLessonMaterialValues[];
    instructions_for_students?: string;
    introduction?: string;
  };
}

const preLessonSchema = yup.object({
  title: yup.string().required("Title is required").min(3, "Title must be at least 3 characters"),
  subject: yup.string().required("Subject is required"),
  topic: yup.string().default(""),
  introduction: yup.string().default(""),
  instructionsForStudents: yup.string().default(""),
});

type PreLessonFormData = yup.InferType<typeof preLessonSchema>;

function EditPreLesson() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { updateLesson } = usePreLessonStore();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [copied, setCopied] = useState(false);
  const [lessonCode, setLessonCode] = useState("");

  const form = useForm<PreLessonFormData>({
    resolver: yupResolver(preLessonSchema),
    defaultValues: {
      title: "",
      subject: "",
      topic: "",
      introduction: "",
      instructionsForStudents: "",
    },
    mode: "onBlur",
  });

  const [editingMaterialId, setEditingMaterialId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");

  useEffect(() => {
    if (!id) {
      setNotFound(true);
      setIsLoading(false);
      return;
    }

    const fetchPreLesson = async () => {
      try {
        const data = await ApiClient.get<PreLessonResponse>(`/pre-lesson/${id}`);
        const lesson = data.pre_lesson;

        form.reset({
          title: lesson.title,
          subject: lesson.subject,
          topic: lesson.topic || "",
          introduction: lesson.introduction || "",
          instructionsForStudents: lesson.instructions_for_students || "",
        });

        setLessonCode(lesson.code);
        setMaterials(
          (lesson.materials || []).map((m) => ({
            id: crypto.randomUUID(),
            title: m.title,
            link: m.link,
            type: m.type || PreLessonMaterialType.LINK,
            s3_key: m.s3_key,
            file_name: m.file_name,
            content_type: m.content_type,
          }))
        );
      } catch {
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPreLesson();
  }, [id, form]);

  const copyStudentLink = async () => {
    const baseUrl = window.location.origin;
    const studentLink = `${baseUrl}/student/${lessonCode}`;
    await navigator.clipboard.writeText(studentLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleUploadComplete = useCallback((uploaded: UploadedFile) => {
    setMaterials((prev) => [
      ...prev,
      {
        id: uploaded.id,
        title: uploaded.name,
        link: uploaded.url,
        type: FileUtils.getTypeFromContentType(uploaded.contentType),
        s3_key: uploaded.key,
        file_name: uploaded.name,
        content_type: uploaded.contentType,
      },
    ]);
  }, []);

  const { isUploading, uploadFiles, error: uploadError } = useFileUpload({
    uploadEndpoint: "/pre-lesson/upload",
    getUploadParams: (file) => ({
      pre_lesson_code: lessonCode,
      file_name: file.name,
      content_type: file.type,
    }),
    onUploadComplete: handleUploadComplete,
    onUploadError: (err) => setSaveError(err.message),
  });

  const removeMaterial = (materialId: string) => {
    setMaterials((prev) => prev.filter((m) => m.id !== materialId));
  };

  const startEditingTitle = (material: Material) => {
    setEditingMaterialId(material.id);
    setEditingTitle(material.title);
  };

  const saveEditingTitle = () => {
    if (editingMaterialId && editingTitle.trim()) {
      setMaterials((prev) =>
        prev.map((m) => (m.id === editingMaterialId ? { ...m, title: editingTitle.trim() } : m))
      );
    }
    setEditingMaterialId(null);
    setEditingTitle("");
  };

  const cancelEditingTitle = () => {
    setEditingMaterialId(null);
    setEditingTitle("");
  };

  const handleFilesSelected = useCallback(
    (files: FileList) => {
      uploadFiles(files);
    },
    [uploadFiles]
  );

  const onSubmit = async (data: PreLessonFormData) => {
    if (!id) return;

    setSaveError(null);
    setIsSaving(true);

    const validMaterials = materials.map((m) => ({
      title: m.title,
      link: m.link,
      type: m.type,
      s3_key: m.s3_key,
      file_name: m.file_name,
      content_type: m.content_type,
    }));

    try {
      await ApiClient.put(`/pre-lesson/${id}`, {
        title: data.title,
        subject: data.subject,
        topic: data.topic || "",
        introduction: data.introduction || undefined,
        instructions_for_students: data.instructionsForStudents || undefined,
        materials: validMaterials.length > 0 ? validMaterials : undefined,
      });

      updateLesson(Number(id), {
        title: data.title,
        subject: data.subject,
        topic: data.topic || "",
        introduction: data.introduction || undefined,
        instructions_for_students: data.instructionsForStudents || undefined,
        materials: validMaterials.length > 0 ? validMaterials : undefined,
      });

      navigate("/");
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-48 mb-8" />
          <div className="space-y-4">
            <div className="h-10 bg-gray-100 rounded" />
            <div className="grid grid-cols-2 gap-4">
              <div className="h-10 bg-gray-100 rounded" />
              <div className="h-10 bg-gray-100 rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="max-w-5xl mx-auto text-center py-12">
        <h2 className="text-xl font-semibold text-foreground mb-2">Pre Lesson not found</h2>
        <p className="text-muted-foreground mb-6">The lesson you're looking for doesn't exist.</p>
        <Link to="/">
          <Button variant="outline" className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to lessons
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <h1 className="text-[26px] font-bold text-foreground">Edit Pre Lesson</h1>
          </div>
          <div className="flex items-center gap-3 ml-8">
            <button
              onClick={copyStudentLink}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-sidebar rounded-md text-[12px] font-mono font-medium text-sidebar-foreground hover:bg-sidebar/80 transition-colors"
            >
              {copied ? (
                <>
                  <Check className="h-3 w-3 text-green-600" />
                  Link copied
                </>
              ) : (
                <>
                  <Copy className="h-3 w-3" />
                  {lessonCode}
                </>
              )}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            className="border-gray-300 text-foreground font-medium gap-2 bg-white"
          >
            <Share2 className="h-4 w-4" />
            Share
          </Button>
        </div>
      </div>

      {(saveError || uploadError) && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {saveError || uploadError}
        </div>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex gap-6">
            <div className="flex-1 space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[13px] font-semibold">
                      Title <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Cell Structure and Function"
                        className="h-10 border-input bg-background text-[14px]"
                        {...field}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="subject"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[13px] font-semibold">
                        Subject <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Science"
                          className="h-10 border-input bg-background text-[14px]"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="topic"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[13px] font-semibold">Topic</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Cell Biology"
                          className="h-10 border-input bg-background text-[14px]"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div>
                <p className="text-[13px] font-semibold text-foreground mb-2">Lesson Materials</p>
                <div className="space-y-3">
                  {materials.length > 0 ? (
                    <div className="space-y-2">
                      {materials.map((material) => (
                        <div
                          key={material.id}
                          className="flex items-center justify-between gap-3 bg-muted/50 border border-input rounded-md px-3 py-2"
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <FileText className="h-4 w-4 text-primary shrink-0" />
                            <div className="flex-1 min-w-0">
                              {editingMaterialId === material.id ? (
                                <div className="flex items-center gap-2">
                                  <Input
                                    value={editingTitle}
                                    onChange={(e) => setEditingTitle(e.target.value)}
                                    className="h-7 text-[13px] flex-1"
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") saveEditingTitle();
                                      if (e.key === "Escape") cancelEditingTitle();
                                    }}
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={saveEditingTitle}
                                    className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                                  >
                                    <Check className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={cancelEditingTitle}
                                    className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              ) : (
                                <>
                                  <p className="text-[13px] font-medium text-foreground truncate">
                                    {material.title}
                                  </p>
                                  <p className="text-[12px] text-muted-foreground truncate">
                                    Uploaded file
                                  </p>
                                </>
                              )}
                            </div>
                          </div>
                          {editingMaterialId !== material.id && (
                            <div className="flex items-center gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => startEditingTitle(material)}
                                className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground hover:bg-muted shrink-0"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeMaterial(material.id)}
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="border border-dashed border-input rounded-md p-4 text-center text-muted-foreground text-[13px]">
                      Upload documents using the panel on the right
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="introduction"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[13px] font-semibold">Introduction</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Any specific notes about how to prepare your students?"
                          rows={4}
                          className="border-input bg-background text-[14px] resize-none"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="instructionsForStudents"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[13px] font-semibold">
                        Instructions for Students
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Assign specific notes or directions to students"
                          rows={4}
                          className="border-input bg-background text-[14px] resize-none"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/")}
                  className="px-6 h-10 border-input text-foreground font-semibold hover:bg-gray-50 bg-white"
                >
                  CANCEL
                </Button>
                <Button
                  type="submit"
                  disabled={isSaving || isUploading}
                  className="px-8 h-10 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                >
                  {isSaving ? "SAVING..." : "SAVE CHANGES"}
                </Button>
              </div>
            </div>

            <div className="w-[300px] shrink-0">
              <FileUpload onFilesSelected={handleFilesSelected} isUploading={isUploading} />
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}

export default EditPreLesson;
